'use client';

import { Button, Drawer, Grid, Heading, TextField, TextPassage } from '@petsmart-ui/sparky';
import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { trackResetPassword } from '@libs/analytics';
import { resetPassword } from '@libs/api-proxy-client';
import { AccountDrawerType, logSentryError, SentryAcpForgotPasswordErrors as SentryErrors, usePageLayout } from '@libs/common-next';
import { IdentityErrorResponse } from '@libs/common-types';
import { getAccountRedirectUrl, isTestEnvironment } from '@libs/core';
import { SnackbarUtils } from '@libs/shared-ui';
import { ResetPasswordConfirmationDrawer } from '../ResetPasswordConfirmationDrawer';
import { ValidationAlerts } from '../ValidationAlert';
import { trackForgotPasswordFormErrors, validateForgotPassword } from './schema';
import type { ForgotPasswordForm } from './type';
import './ForgotPasswordDrawer.scss';
const initialFormValues: ForgotPasswordForm = {
  email: ''
};
export function ForgotPasswordDrawer() {
  const [isFetching, setIsFetching] = useState(false);
  const [showAlert, setShowAlert] = useState({
    status: false,
    showWentWrong: false
  });
  const wasSignInOpenRef = useRef<boolean>(false);
  const {
    accountDrawersOpen,
    removeAccountDrawers,
    openAccountDrawer
  } = usePageLayout();
  const isForgotPasswordOpen = accountDrawersOpen?.includes(AccountDrawerType.FORGOT_PASSWORD);
  useEffect(() => {
    setShowAlert({
      status: false,
      showWentWrong: false
    });
  }, []);
  useEffect(() => {
    if (isForgotPasswordOpen) {
      const wasSignInOpen = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN) ?? false;
      if (wasSignInOpen && !wasSignInOpenRef.current) {
        wasSignInOpenRef.current = true;
        removeAccountDrawers([AccountDrawerType.SIGN_IN]);
      }
    } else {
      wasSignInOpenRef.current = false;
    }
  }, [isForgotPasswordOpen, accountDrawersOpen, removeAccountDrawers]);
  async function handleResetPassword({
    email
  }: ForgotPasswordForm) {
    if (isFetching) return;
    trackResetPassword();
    setIsFetching(true);
    setShowAlert({
      status: false,
      showWentWrong: false
    });
    try {
      const isTestEnv = isTestEnvironment(globalThis.location.hostname);
      const redirectUri = getAccountRedirectUrl(isTestEnv);
      const resResetPassword = await resetPassword({
        email,
        redirectUri
      });
      if (resResetPassword instanceof Error) {
        SnackbarUtils.showPredefined('SERVER_ERROR');
        logSentryError(resResetPassword, SentryErrors.ForgotPassword.SubmitFailed);
        return;
      }
      if (resResetPassword && 'status' in resResetPassword && typeof resResetPassword.status === 'number' && resResetPassword.status >= 500) {
        SnackbarUtils.showPredefined('SERVER_ERROR');
        logSentryError(new Error(`Server Error: ${resResetPassword.status}`), SentryErrors.ForgotPassword.SubmitFailed);
        return;
      }
      if (resResetPassword && 'code' in resResetPassword && resResetPassword.code === 400) {
        const errorDetails = resResetPassword as IdentityErrorResponse;
        const hasTooManyAttemptsReason = Array.isArray(errorDetails.data?.reasons) && errorDetails.data.reasons.includes('TOO_MANY_ATTEMPTS');
        const hasTooManyAttemptsCode = 'errorCode' in errorDetails && (errorDetails.errorCode === 390 || errorDetails.errorCode === 319);
        if (hasTooManyAttemptsReason || hasTooManyAttemptsCode) {
          setShowAlert({
            status: true,
            showWentWrong: false
          });
        } else {
          setShowAlert({
            status: true,
            showWentWrong: true
          });
        }
        logSentryError(new Error(errorDetails.description), SentryErrors.ForgotPassword.SubmitFailed);
        return;
      }
      if (resResetPassword && 'status' in resResetPassword && resResetPassword.status === 'ok') {
        openAccountDrawer(AccountDrawerType.RESET_PASSWORD_CONFIRMATION);
        return;
      }
      throw new Error('Received an unexpected response from the server.');
    } catch (err) {
      SnackbarUtils.showPredefined('SERVER_ERROR');
      logSentryError(err, SentryErrors.ForgotPassword.SubmitFailed);
    } finally {
      setIsFetching(false);
    }
  }
  return <Formik onSubmit={handleResetPassword} initialValues={initialFormValues} validate={validateForgotPassword} validateOnBlur>
            {({
      errors,
      values,
      handleChange,
      handleBlur,
      touched,
      resetForm
    }) => <>
                    <Drawer data-testid="forgot-password-drawer" align="right" isActive={accountDrawersOpen?.includes(AccountDrawerType.FORGOT_PASSWORD)} onClose={() => {
        removeAccountDrawers([AccountDrawerType.FORGOT_PASSWORD]);
        if (wasSignInOpenRef.current) {
          openAccountDrawer(AccountDrawerType.SIGN_IN);
          wasSignInOpenRef.current = false;
        }
        setShowAlert({
          status: false,
          showWentWrong: false
        });
        resetForm();
      }} ariaLabelledBy="drawer-heading-1" className="forgot-password-drawer">
                        <Drawer.Header>
                            <Heading data-testid="forgot-password-drawer-heading-1" id="drawer-heading-1" tagName="h2" size="title">
                                Forgot password
                            </Heading>
                        </Drawer.Header>
                        <Drawer.Body>
                            <Form noValidate>
                                <Grid>
                                    <Grid.Item>
                                        {showAlert.status && <ValidationAlerts defaultHeading={showAlert.showWentWrong ? 'Something went wrong' : 'Too many attempts'} validationErrors={[{
                  message: showAlert.showWentWrong ? 'Please try again' : 'Please wait 1-2 minutes and try again.'
                }]} />}
                                        <TextPassage>
                                            Enter the email address associated with your Treats Rewards account and we
                                            will send you a link to reset your password.
                                        </TextPassage>
                                    </Grid.Item>
                                    <Grid.Item>
                                        <TextField data-testid="forgot-drawer-email" data-cs-mask id="forgot-drawer-email" label="Email address" type="email" name="email" onChange={handleChange} onBlur={handleBlur} isError={touched.email && !!errors.email} fieldNote={touched.email ? errors.email : ''} value={values.email} />
                                    </Grid.Item>
                                    <Grid.Item>
                                        <Button data-testid="reset-password-btn" hideText={isFetching} fullWidth loading={isFetching} size="lg" text="Reset password" variant="primary" onClick={() =>
                // Error tracking for Data Layer should fire on click
                // because form validation runs onChange and spams Data Layer
                trackForgotPasswordFormErrors(values)} />
                                    </Grid.Item>
                                </Grid>
                            </Form>
                        </Drawer.Body>
                    </Drawer>
                    <ResetPasswordConfirmationDrawer email={values.email} />
                </>}
        </Formik>;
}